<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Upload QR Databin Component
      p.text--secondary A component that creates databins and generates QR codes for cross-device file uploads. Works with YzDatabinSync for file synchronization.

    // Basic Usage Examples
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in uploadQrVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            yz-upload-qr-databin(
              v-if="variation.id === 'basic-qr'"
              v-bind="variation.props"
              @databin-created="handleDatabinCreated"
              @error="handleError"
            )
            yz-upload-qr-databin(
              v-else-if="variation.id === 'large-qr'"
              v-bind="variation.props"
              @databin-created="handleDatabinCreated"
              @error="handleError"
            )
            yz-upload-qr-databin(
              v-else-if="variation.id === 'custom-timing'"
              v-bind="variation.props"
              @databin-created="handleDatabinCreated"
              @error="handleError"
            )

            .mt-4
              h5.mb-2 QR Code Status:
              .text-caption.text--secondary(
                v-if="databinKeys[variation.id]"
              ) Databin Key: {{ databinKeys[variation.id] }}
              .text-caption.text--secondary(
                v-else
              ) Creating databin...

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}



    // Integration Examples
    .component-section.mb-8#integration-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('integration-examples')") Integration with YzDatabinSync
      .mb-4
        p The YzUploadQrDatabin component works seamlessly with YzDatabinSync for centralized databin management and automatic synchronization across multiple upload components.

        h4.mt-4.mb-3 How It Works
        .pl-4.border-l-4.border-primary.bg-grey-50.pa-3.rounded-r
          p.mb-2.font-weight-medium.primary--text Integration Flow:
          ol.ml-4
            li.mb-2
              strong YzUploadQrDatabin creates a databin
              | : When mounted, it generates a unique databin key and QR code for mobile uploads
            li.mb-2
              strong YzDatabinSync manages the databin lifecycle
              | : It handles polling, synchronization, and automatic cleanup of the databin
            li.mb-2
              strong Multiple file sources converge
              | : Files from QR uploads, direct uploads, and other sources all sync through the same databin
            li.mb-2
              strong Real-time updates
              | : All components stay in sync automatically through polling and reactive updates
            li.mb-2
              strong Automatic cleanup
              | : When components unmount, the databin is cleaned up and polling stops

      .variation-item.mb-6#databin-sync-integration
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('databin-sync-integration')") Centralized Databin Management
          p.text--secondary.caption Using YzDatabinSync as a renderless component for automatic state synchronization

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .d-flex.flex-column.gap-4
            .d-flex.flex-column.flex-md-row.gap-4
              .flex-grow-1
                h5.mb-3 Direct Upload
                // Files uploaded here go directly to directUploadFiles array
                yz-upload-card(
                  v-model="directUploadFiles"
                  primary-title="Upload from this device"
                  description="Click or drag files here"
                  @error="handleError"
                  upload-mode="immediate"
                  multiple
                  append-files
                  @upload-batch-complete="handleUploadBatchComplete"
                )

              .d-flex.align-center.justify-center.px-3
                .text--secondary OR

              .flex-grow-1.d-flex.flex-column.align-center
                h5.mb-3.text-center Mobile Upload
                // Creates databin and QR code - mobile uploads go to the databin
                yz-upload-qr-databin(
                  :qr-size="100"
                  @databin-created="handleIntegrationDatabinCreated"
                  @error="handleError"
                )
                .text-caption.text-center.mt-2.text--secondary Scan with phone

            // CENTRAL SYNC COMPONENT: Merges directUploadFiles + databin files into allSyncedFiles
            // This renderless component handles all databin polling and synchronization
            yz-databin-sync(
              v-if="integrationDatabinKey"
              v-model="allSyncedFiles"
              :added-items.sync="directUploadFiles"
              :databin-key="integrationDatabinKey"
              @error="handleError"
              ref="databinSync"
            )

          template(v-if="allSyncedFiles.length > 0")
            .mt-4
              h5.mb-2 All Files ({{ allSyncedFiles.length }}):
              yz-file-preview(v-model="allSyncedFiles" delete-mode="immediate" @deleted="handleDeletedItems")

          .mt-3.text-caption.text--secondary
            | Databin Key: {{ integrationDatabinKey || 'Creating...' }}
            br
            | Polling: {{ isPolling ? 'Active' : 'Inactive' }}
            br
            | Last Sync: {{ lastSync || 'Never' }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === databinSyncExample ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === databinSyncExample ? 'success' : ''" @click="copyCode(databinSyncExample)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ databinSyncExample }}

    // Best Practices & Tips
    .component-section.mb-8#best-practices
      h3.mb-4.cursor-pointer(@click="navigateToSection('best-practices')") Best Practices & Integration Tips

      .mb-4
        h4.mb-3.text-primary ✅ Do's
        .pl-4.border-l-4.border-success.bg-green-50.pa-3.rounded-r
          ul.ml-4
            li.mb-2
              strong Use YzDatabinSync for centralized management
              | : Let the sync component handle all databin lifecycle and polling
            li.mb-2
              strong Always provide error handlers
              | : Both components can emit errors that need user feedback
            li.mb-2
              strong Use :added-items.sync for local files
              | : This ensures proper two-way binding with the sync component
            li.mb-2
              strong Clean up in beforeDestroy
              | : Clear any intervals you create for status tracking
            li.mb-2
              strong Test on mobile devices
              | : QR codes need to work well on different screen sizes

      .mb-4
        h4.mb-3.text-error ❌ Don'ts
        .pl-4.border-l-4.border-error.bg-red-50.pa-3.rounded-r
          ul.ml-4
            li.mb-2
              strong Don't manage databin lifecycle manually
              | : Let YzDatabinSync handle creation, polling, and cleanup
            li.mb-2
              strong Don't mix file sources without sync
              | : Use the sync component to merge files from different sources
            li.mb-2
              strong Don't forget to handle TTL expiration
              | : Set appropriate TTL values and handle databin expiration gracefully
            li.mb-2
              strong Don't poll too aggressively
              | : Default polling interval is optimized for performance vs responsiveness

      .mb-4
        h4.mb-3.text-info 💡 Common Patterns
        .code-example.mb-4
          h5.mb-2 Basic Integration Pattern
          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicPatternExample }}

        .code-example.mb-4
          h5.mb-2 Multiple Upload Sources
          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ multipleSourcesExample }}

    // QR Code Features
    .component-section.mb-8#qr-features
      h3.mb-4.cursor-pointer(@click="navigateToSection('qr-features')") QR Code & Databin Features
      .mb-4
        p The YzUploadQrDatabin component provides the following features:
        ul.ml-4
          li Generates QR codes that link to a mobile-optimized upload page
          li Real-time file synchronization using databin technology
          li Configurable polling intervals for responsive updates
          li Automatic databin cleanup and TTL management
          li Visual connection status indicators
          li Support for all file types through the mobile interface

      .code-example.mb-4
        h4.mb-2 Basic QR Databin Usage
        pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
          code yz-upload-qr-databin(v-model="files")

      .code-example.mb-4
        h4.mb-2 Custom Configuration
        pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
          code {{ advancedExample }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .mb-4
        p.mb-2 This component works with:
        ul.ml-4
          li
            strong YzUploadQrDatabin
            | : Creates databin and QR code for mobile uploads
          li
            strong
              | YzDatabinSync
              code.text-caption.ml-1.bg-grey-100.pa-1.rounded @/src/components/yds/YzDatabinSync.vue
            | : Manages databin lifecycle and file synchronization

      h4.mb-3 YzUploadQrDatabin Props
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in uploadQrProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3.mt-6 YzDatabinSync Props
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in databinSyncProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      h4.mb-3 YzUploadQrDatabin Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in uploadQrEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

      h4.mb-3.mt-6 YzDatabinSync Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in databinSyncEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

    // Slots Documentation
    .component-section.mb-8#slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('slots')") Slots
      .slots-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in uploadQrSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

    // Methods Documentation
    .component-section.mb-8#methods
      h3.mb-4.cursor-pointer(@click="navigateToSection('methods')") Public Methods
      h4.mb-3 YzUploadQrDatabin Methods
      .methods-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Parameters
                th Returns
                th Description
            tbody
              tr(v-for="method in uploadQrMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.text--secondary.text-no-wrap {{ method.parameters }}
                td.text--secondary.text-no-wrap {{ method.returns }}
                td.single-line {{ method.description }}

      h4.mb-3.mt-6 YzDatabinSync Methods
      .methods-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Parameters
                th Returns
                th Description
            tbody
              tr(v-for="method in databinSyncMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.text--secondary.text-no-wrap {{ method.parameters }}
                td.text--secondary.text-no-wrap {{ method.returns }}
                td.single-line {{ method.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      databinKeys: {
        'basic-qr': null,
        'large-qr': null,
        'custom-timing': null
      },
      // YzDatabinSync integration data
      allSyncedFiles: [],
      directUploadFiles: [],
      integrationDatabinKey: null,
      isPolling: false,
      lastSync: null,
      uploadQrVariations: [
        {
          name: 'Basic QR Databin',
          id: 'basic-qr',
          description: 'Default QR code that creates databin and displays QR',
          props: {},
          code: 'yz-upload-qr-databin(@databin-created="handleDatabin")'
        },
        {
          name: 'Large QR Code',
          id: 'large-qr',
          description: 'Larger QR code for better scanning visibility',
          props: {
            qrSize: 180
          },
          code: `yz-upload-qr-databin(
  :qr-size="180"
  @databin-created="handleDatabin"
)`
        },
        {
          name: 'Custom TTL',
          id: 'custom-timing',
          description: 'Custom databin TTL settings',
          props: {
            ttl: 1800
          },
          code: `yz-upload-qr-databin(
  :ttl="1800"
  @databin-created="handleDatabin"
)`
        }
      ],
      uploadQrProps: [
        {
          name: 'qrSize',
          type: 'Number',
          default: '120',
          description: 'Size of the QR code in pixels (width and height)'
        },
        {
          name: 'ttl',
          type: 'Number',
          default: '900',
          description: 'Time to live for the databin in seconds (15 minutes default)'
        },
        {
          name: 'autoStart',
          type: 'Boolean',
          default: 'true',
          description: 'Automatically create databin when component mounts'
        }
      ],
      uploadQrEvents: [
        {
          name: 'databin-created',
          payload: 'Object',
          description: 'Emitted when databin is successfully created, contains {key, url} object'
        },
        {
          name: 'error',
          payload: 'Error',
          description: 'Emitted when an error occurs during databin creation'
        }
      ],
      uploadQrSlots: [],
      uploadQrMethods: [
        {
          name: 'initialize()',
          parameters: '-',
          returns: 'Promise<void>',
          description: 'Manually create databin. Useful when autoStart is false.'
        },
        {
          name: 'getDatabinKey()',
          parameters: '-',
          returns: 'String',
          description: 'Get the current databin key.'
        }
      ],
      databinSyncProps: [
        {
          name: 'value (v-model)',
          type: 'Array',
          default: '[]',
          description: 'Array of all files - merged from databin and added items'
        },
        {
          name: 'addedItems.sync',
          type: 'Array',
          default: '[]',
          description: 'Local files to be added to databin (two-way binding)'
        },
        {
          name: 'databinKey',
          type: 'String',
          default: 'required',
          description: 'The databin key to synchronize with'
        },
        {
          name: 'ttl',
          type: 'Number',
          default: '900',
          description: 'Time to live for the databin in seconds'
        },
        {
          name: 'pollingInterval',
          type: 'Number',
          default: '4000',
          description: 'Polling interval in milliseconds for databin updates'
        },
        {
          name: 'databinUrl',
          type: 'String',
          default: '/databin/',
          description: 'Base URL for databin API endpoints'
        },
        {
          name: 'databinMethod',
          type: 'String',
          default: 'PUT',
          description: 'HTTP method for databin sync operations'
        },
        {
          name: 'databinHeaders',
          type: 'Object',
          default: '{}',
          description: 'Additional headers for databin API requests'
        }
      ],
      databinSyncEvents: [
        {
          name: 'input',
          payload: 'Array',
          description: 'Emitted when merged file array changes (v-model support)'
        },
        {
          name: 'update:addedItems',
          payload: 'Array',
          description: 'Emitted when added items array changes (.sync support)'
        },
        {
          name: 'error',
          payload: 'Object',
          description: 'Emitted when databin operations fail with error details'
        }
      ],
      databinSyncMethods: [
        {
          name: 'initialize()',
          parameters: '-',
          returns: 'Promise<void>',
          description: 'Initialize databin sync and start polling'
        },
        {
          name: 'addItems(items)',
          parameters: 'Array|Object',
          returns: 'Promise<void>',
          description: 'Add files to databin with optimistic updates'
        },
        {
          name: 'removeItems(items)',
          parameters: 'Array|Object',
          returns: 'Promise<void>',
          description: 'Remove files from databin with optimistic updates'
        },
        {
          name: 'updateItems(items)',
          parameters: 'Array|Object',
          returns: 'Promise<void>',
          description: 'Update existing files in databin'
        },
        {
          name: 'manualSync()',
          parameters: '-',
          returns: 'Promise<void>',
          description: 'Manually fetch latest data from databin'
        },
        {
          name: 'forceSync()',
          parameters: '-',
          returns: 'Promise<void>',
          description: 'Force push current data to databin'
        },
        {
          name: 'getDatabinKey()',
          parameters: '-',
          returns: 'String',
          description: 'Get the current databin key'
        },
        {
          name: 'getIsPolling()',
          parameters: '-',
          returns: 'Boolean',
          description: 'Check if polling is currently active'
        },
        {
          name: 'getLastSync()',
          parameters: '-',
          returns: 'String',
          description: 'Get timestamp of last successful sync'
        },
        {
          name: 'getPendingOperations()',
          parameters: '-',
          returns: 'Number',
          description: 'Get count of pending databin operations'
        }
      ],
      combinedExample: `// Template
.d-flex.flex-column.flex-md-row.gap-4
  .flex-grow-1
    h5.mb-3 Direct Upload
    yz-upload-card(
      v-model="directFiles"
      primary-title="Upload from this device"
      description="Click or drag files here"
    )

  .d-flex.align-center.justify-center.px-3
    .text--secondary OR

  .flex-grow-1.d-flex.flex-column.align-center
    h5.mb-3.text-center Mobile Upload
    yz-upload-qr-databin(
      v-model="mobileFiles"
      :qr-size="100"
      no-title
      no-description
    )
    .text-caption.text-center.mt-2.text--secondary Scan with phone

// Script
computed: {
  allFiles() {
    return [...this.directFiles, ...this.mobileFiles]
  }
}`,
      advancedExample: `yz-upload-qr-databin(
  :qr-size="150"
  :ttl="1800"
  :auto-start="true"
  @databin-created="onDatabinCreated"
  @error="onError"
)`,
      databinSyncExample: `<!-- TEMPLATE: Complete Integration Example -->

<!-- 1. QR UPLOAD COMPONENT: Creates the databin and QR code -->
yz-upload-qr-databin(
  @databin-created="onDatabinReady"
  @error="handleError"
)

<!-- 2. DIRECT UPLOAD AREA -->
.d-flex.flex-column.flex-md-row.gap-4
  .flex-grow-1
    h5.mb-3 Direct Upload
    yz-upload-card(
      v-model="localFiles"
      primary-title="Upload from this device"
      upload-mode="immediate"
      multiple
      append-files
      @upload-batch-complete="addFilesToDatabin"
    )

  .flex-grow-1.text-center
    h5.mb-3 Mobile Upload
    p.text-caption.text--secondary Scan the QR code above to upload from mobile

<!-- 3. CENTRAL DATABIN SYNC: Manages all file state -->
yz-databin-sync(
  v-if="databinKey"
  v-model="allFiles"
  :added-items.sync="localFiles"
  :databin-key="databinKey"
  @error="handleError"
  ref="databinSync"
)

<!-- 4. FILE PREVIEW: Shows all files from all sources -->
template(v-if="allFiles.length > 0")
  .mt-4
    h5.mb-2 All Files ({{ allFiles.length }})
    yz-file-preview(
      v-model="allFiles"
      delete-mode="immediate"
      @deleted="removeFilesFromDatabin"
    )

/* ===== SCRIPT: Complete Integration Logic ===== */

data() {
  return {
    // State variables
    databinKey: null,           // Databin key from QR component
    localFiles: [],             // Files uploaded directly
    allFiles: [],               // Combined files (managed by YzDatabinSync)

    // Status tracking (optional)
    isPolling: false,
    lastSyncTime: null
  }
},

methods: {
  // STEP 1: Handle databin creation from QR component
  onDatabinReady(databinInfo) {
    this.databinKey = databinInfo.key
    console.log('✅ Databin ready:', this.databinKey)

    // Optional: Start tracking sync status
    this.$nextTick(() => this.trackSyncStatus())
  },

  // STEP 2: Add locally uploaded files to databin
  addFilesToDatabin(uploadResults) {
    if (this.$refs.databinSync && uploadResults.length > 0) {
      console.log('📤 Adding files to databin:', uploadResults.length)
      this.$refs.databinSync.addItems(uploadResults)
    }
  },

  // STEP 3: Handle file deletions
  removeFilesFromDatabin(deletedFiles) {
    if (this.$refs.databinSync && deletedFiles.length > 0) {
      console.log('🗑️ Removing files from databin:', deletedFiles.length)
      this.$refs.databinSync.removeItems(deletedFiles)
    }
  },

  // OPTIONAL: Track sync status for UI feedback
  trackSyncStatus() {
    this.statusInterval = setInterval(() => {
      if (this.$refs.databinSync) {
        this.isPolling = this.$refs.databinSync.getIsPolling()
        this.lastSyncTime = this.$refs.databinSync.getLastSync()
      }
    }, 1000)
  },

  // Error handling
  handleError(error) {
    console.error('❌ Databin error:', error)
    // Show user-friendly error message
    this.$store.dispatch('showMessage', {
      message: 'File sync error: ' + error.message,
      type: 'error'
    })
  }
},

// Cleanup
beforeDestroy() {
  if (this.statusInterval) {
    clearInterval(this.statusInterval)
  }
}

/* ===== KEY CONCEPTS ===== */

// 🎯 DATA FLOW:
// 1. YzUploadQrDatabin creates databin → emits 'databin-created'
// 2. YzDatabinSync uses databin-key to manage lifecycle
// 3. Local files go to 'localFiles' → sync adds them via 'added-items.sync'
// 4. Mobile files go directly to databin → sync fetches via polling
// 5. YzDatabinSync merges all sources → outputs to 'allFiles' via v-model

// 💡 WHY THIS WORKS:
// - YzDatabinSync handles all the complex databin lifecycle management
// - Files from different sources automatically merge and stay in sync
// - Real-time updates via polling keep all clients synchronized
// - Automatic cleanup prevents memory leaks`,
      basicPatternExample: `// Minimal setup for QR + Direct upload
data() {
  return {
    databinKey: null,
    localFiles: [],
    allFiles: []
  }
},
methods: {
  onDatabinReady(data) {
    this.databinKey = data.key
  },
  addToDatabin(files) {
    this.$refs.sync.addItems(files)
  }
}`,
      multipleSourcesExample: `// Pattern for multiple upload sources
data() {
  return {
    databinKey: null,
    directFiles: [],      // From drag & drop
    cameraFiles: [],      // From camera capture
    clipboardFiles: [],   // From paste
    allFiles: []          // Merged by YzDatabinSync
  }
},
methods: {
  onDatabinReady(data) { this.databinKey = data.key },
  addDirectFiles(files) { this.$refs.sync.addItems(files) },
  addCameraFiles(files) { this.$refs.sync.addItems(files) },
  addClipboardFiles(files) { this.$refs.sync.addItems(files) }
}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
    // Set up periodic updates for YzDatabinSync state
    this.stateUpdateInterval = setInterval(() => {
      this.updateDatabinSyncState()
    }, 1000)
  },
  beforeDestroy() {
    if (this.stateUpdateInterval) {
      clearInterval(this.stateUpdateInterval)
    }
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    handleDatabinCreated(data) {
      console.log('Databin created:', data)
      // This is for individual variation demos
    },
    handleIntegrationDatabinCreated(data) {
      this.integrationDatabinKey = data.key
      console.log('Integration databin created:', data.key)
      // Update YzDatabinSync state
      this.$nextTick(() => {
        this.updateDatabinSyncState()
      })
    },
    handleUploadBatchComplete(uploadResults) {
      // When files are uploaded, add them to the databin sync
      if (this.$refs.databinSync && uploadResults.length > 0) {
        console.log('UploadBatchComplete:', uploadResults)
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    handleError(error) {
      console.error('QR Databin error:', error)
    },
    // Update YzDatabinSync state periodically
    updateDatabinSyncState() {
      if (this.$refs.databinSync) {
        this.isPolling = this.$refs.databinSync.getIsPolling()
        this.lastSync = this.$refs.databinSync.getLastSync()
      }
    },
    handleDeletedItems(deletedItems) {
      console.log('Items deleted:', deletedItems)
      if (this.$refs.databinSync && deletedItems.length > 0) {
        this.$refs.databinSync.removeItems(deletedItems)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table,
.events-table,
.slots-table,
.methods-table {
  overflow-x: auto;
}

.props-table .v-data-table,
.events-table .v-data-table,
.slots-table .v-data-table,
.methods-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.gap-2 > * {
  margin: 0;
}

.gap-4 {
  gap: 16px;
}

.gap-4 > * {
  margin: 0;
}
</style>
